import { Flex, Stack, useMantineTheme } from '@mantine/core';
import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IGoal, Targeting, TAttributionModel } from '@feathr/blackbox';
import { SegmentedControl, StatBox, TableStatsCard, Text, Tooltip } from '@feathr/components';
import { getIconForAction, useToggle } from '@feathr/hooks';

import { attributionModelLabels } from '../AttributionModel/AttributionModel';
import ExportModal from '../ExportModal';
import CategoryFilter from './CategoryFilter';
import {
  ConversionAnalyticsColumns,
  defaultColumnIds as defaultConversionAnalyticsColumnIds,
} from './ConversionsTable/ConversionAnalyticsColumns';
import ConversionsTable from './ConversionsTable/ConversionsTable';
import { isModelRerunning } from './ConversionTableCard.config';
import { useConversionsData } from './ConversionTableCard.hooks';
import type { IConversionTableCardProps, TProductModel } from './ConversionTableCard.types';
import {
  CONVERSION_TRACKING_TOOLTIPS,
  formatCurrency,
  formatNumber,
  getConversionTrackingModel,
} from './ConversionTableCard.utils';
import ConversionTableCardToolbar from './ConversionTableCardToolbar';
import { ConversionTableCardV1, RerunModal } from './ConversionTableCardV1';
import type { TContext } from './ConversionTableCardV1/RerunModal';
import GoalsTable from './GoalsTable/GoalsTable';

type TActiveTab = 'conversions' | 'goals';

interface IConversionTableCardComponentProps extends IConversionTableCardProps {
  attributionModel: TAttributionModel;
  end: string;
  goals: IGoal[];
  model: TProductModel;
  start: string;
  targetings: IObservableArray<Targeting>;
  localUrl?: (url: string) => string;
}

function ConversionTableCard({
  attributionModel,
  end,
  goals,
  model,
  start,
  targetings,
  localUrl,
}: Readonly<IConversionTableCardComponentProps>): JSX.Element {
  const [activeTab, setActiveTab] = useState<TActiveTab>('conversions');
  const [isExportModalOpen, toggleExportModalOpen] = useToggle(false);
  const [isRerunModalOpen, toggleRerunModalOpen] = useToggle(false);
  const { t } = useTranslation();
  const { spacing } = useMantineTheme();

  const {
    conversions,
    isLoading,
    filters,
    pagination,
    sort,
    columnIds,
    handleCategoryChange,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    handleColumnIdsChange,
    handleExport,
    handleRerun,
  } = useConversionsData({
    model,
    start,
    end,
    attributionModel,
    targetings,
    localUrl,
  });

  function handleTabChange(value: string): void {
    setActiveTab(value as TActiveTab);
  }

  const columns = ConversionAnalyticsColumns(goals, localUrl);
  const conversionTrackingModel = getConversionTrackingModel(goals);

  const totalValue = conversions.models.reduce(
    (sum, conv) => sum + (conv.get('computed_value') ?? 0),
    0,
  );
  const totalCount = conversions.pagination?.count ?? conversions.models.length;

  if (isModelRerunning(model)) {
    return (
      <TableStatsCard title={t('Conversion Analytics')}>
        <Text.DefaultRegular>
          {t('Recalculating conversions, please check again in a few minutes!')}
        </Text.DefaultRegular>
      </TableStatsCard>
    );
  }

  return (
    <>
      <TableStatsCard title={t('Conversion Analytics')}>
        <Stack gap={spacing[3]}>
          <Flex gap={spacing[4]}>
            <StatBox
              border={'full'}
              label={t('Conversion value')}
              primary={formatCurrency(totalValue)}
              tooltip={t('Total value of all conversions')}
            />
            <StatBox
              border={'full'}
              label={t('Conversions')}
              primary={formatNumber(totalCount)}
              tooltip={t('Total number of conversions')}
            />
          </Flex>

          <Flex align={'center'} justify={'space-between'}>
            <SegmentedControl
              data={[
                { label: t('Conversions'), value: 'conversions' },
                { label: t('Goals'), value: 'goals' },
              ]}
              onChange={handleTabChange}
              value={activeTab}
            />
            <ConversionTableCardToolbar
              activeTab={activeTab}
              columnIds={
                activeTab === 'conversions' ? columnIds : defaultConversionAnalyticsColumnIds
              }
              columns={columns}
              localUrl={localUrl}
              model={model}
              toggleExportModal={toggleExportModalOpen}
              toggleRerunModal={toggleRerunModalOpen}
              updateColumnIds={handleColumnIdsChange}
            />
          </Flex>

          <Flex align={'center'} justify={'space-between'}>
            {activeTab === 'conversions' && (
              <CategoryFilter
                attributionModel={attributionModel}
                filters={filters}
                label={''}
                model={model}
                onSelectCategory={handleCategoryChange}
                values={filters.category__in}
              />
            )}
            {activeTab === 'goals' && <div />}
            <Flex align={'center'} gap={spacing[2]}>
              <Text.DefaultHeadingMedium>{t('Attribution model')}:</Text.DefaultHeadingMedium>
              <Text.DefaultRegular>
                {attributionModelLabels[attributionModel].label}
              </Text.DefaultRegular>
              <Text.DefaultRegular>·</Text.DefaultRegular>
              {goals.length > 0 && conversionTrackingModel && (
                <>
                  <Text.DefaultSemibold>{t('Conversion tracking')}:</Text.DefaultSemibold>
                  <Text.DefaultRegular>{conversionTrackingModel}</Text.DefaultRegular>
                  <Tooltip title={t(CONVERSION_TRACKING_TOOLTIPS[conversionTrackingModel])}>
                    {getIconForAction('info')}
                  </Tooltip>
                </>
              )}
            </Flex>
          </Flex>

          {activeTab === 'conversions' && (
            <ConversionsTable
              columnIds={columnIds}
              columns={columns}
              idKey={'id'}
              isLoading={isLoading}
              items={conversions.models}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onSortChange={handleSortChange}
              page={pagination.page}
              pages={conversions.pagination?.pages ?? 1}
              pageSize={pagination.page_size}
              sort={sort}
            />
          )}

          {activeTab === 'goals' && (
            <GoalsTable attributionModel={attributionModel} goals={goals} model={model} />
          )}
        </Stack>
      </TableStatsCard>
      {isExportModalOpen && (
        <ExportModal
          onClose={toggleExportModalOpen}
          onConfirm={handleExport}
          title={t('Export Conversions')}
        />
      )}
      {isRerunModalOpen && (
        <RerunModal
          context={model.className === 'Event' ? 'Project' : (model.className as TContext)}
          id={model.id}
          name={model.name}
          onClose={toggleRerunModalOpen}
          onConfirm={handleRerun}
        />
      )}
    </>
  );
}

ConversionTableCard.V1 = ConversionTableCardV1;

export default observer(ConversionTableCard);
